// Mirrors Recipe#cost_breakdown and Recipe#margin_from_msrp for free-typed line items.

export const EXPENSE_TYPES = [
  { value: "food", label: "Food", colorClass: "food-cost" },
  { value: "labor", label: "Labor", colorClass: "labor-cost" },
  { value: "packaging", label: "Packaging", colorClass: "packaging-cost" },
  { value: "overhead", label: "Overhead", colorClass: "overhead-cost" },
];

export const UNIT_FAMILIES = {
  weight: {
    label: "Weight",
    units: [
      { value: "gram", label: "g", ratio: 1 },
      { value: "oz", label: "oz", ratio: 28.3495 },
      { value: "lb", label: "lb", ratio: 453.592 },
      { value: "kg", label: "kg", ratio: 1000 },
    ],
  },
  volume: {
    label: "Volume",
    units: [
      { value: "tsp", label: "tsp", ratio: 4.92892 },
      { value: "tbsp", label: "tbsp", ratio: 14.7868 },
      { value: "fl oz", label: "fl oz", ratio: 29.5735 },
      { value: "cup", label: "cup", ratio: 236.588 },
      { value: "pt", label: "pint", ratio: 473.176 },
      { value: "qt", label: "quart", ratio: 946.353 },
      { value: "gal", label: "gallon", ratio: 3785.41 },
      { value: "ml", label: "ml", ratio: 1 },
      { value: "l", label: "liter", ratio: 1000 },
    ],
  },
  count: {
    label: "Count",
    units: [{ value: "each", label: "each", ratio: 1 }],
  },
  time: {
    label: "Time",
    units: [
      { value: "hour", label: "hour", ratio: 1 },
      { value: "minute", label: "minute", ratio: 1 / 60 },
    ],
  },
};

export const CURRENCIES = [
  { value: "USD", symbol: "$" },
  { value: "CAD", symbol: "$" },
  { value: "GBP", symbol: "£" },
  { value: "EUR", symbol: "€" },
];

const DEFAULT_UNIT_BY_TYPE = {
  food: "lb",
  labor: "hour",
  packaging: "each",
  overhead: "each",
};

const UNITS_BY_VALUE = Object.entries(UNIT_FAMILIES).reduce((all, [family, { units }]) => {
  units.forEach((unit) => {
    all[unit.value] = { ...unit, family };
  });
  return all;
}, {});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const familyForUnit = (unit) => UNITS_BY_VALUE[unit]?.family ?? null;

const unitRatio = (unit) => UNITS_BY_VALUE[unit]?.ratio ?? 0;

const unitsForFamily = (family) => UNIT_FAMILIES[family]?.units ?? [];

export const compatibleUnits = (purchaseUnit) => unitsForFamily(familyForUnit(purchaseUnit));

export const defaultUnitForType = (expenseType) => DEFAULT_UNIT_BY_TYPE[expenseType] ?? "lb";

export const currencySymbol = (currency) =>
  CURRENCIES.find(({ value }) => value === currency)?.symbol ?? "$";

export const costPerBaseUnit = (line) => {
  const purchased = toNumber(line.purchaseAmount) * unitRatio(line.purchaseUnit);
  if (purchased <= 0) return 0;

  return (toNumber(line.purchasePrice) + toNumber(line.purchaseShipping)) / purchased;
};

export const lineCostPerBatch = (line) => {
  if (familyForUnit(line.usedUnit) !== familyForUnit(line.purchaseUnit)) return 0;

  return toNumber(line.usedAmount) * unitRatio(line.usedUnit) * costPerBaseUnit(line);
};

export const lineCostPerPackage = (line, packages) => {
  const packageCount = Math.max(toNumber(packages), 0);
  return packageCount > 0 ? lineCostPerBatch(line) / packageCount : 0;
};

export const costBreakdown = (lines, packages) => {
  const packageCount = Math.max(toNumber(packages), 0);
  const costs = { food: 0, labor: 0, packaging: 0, overhead: 0 };

  lines.forEach((line) => {
    if (costs[line.expenseType] === undefined) return;

    costs[line.expenseType] += lineCostPerBatch(line);
  });

  const total = costs.food + costs.labor + costs.packaging + costs.overhead;

  return {
    ...costs,
    total,
    perPackage: packageCount > 0 ? total / packageCount : 0,
  };
};

export const percentOf = (part, total) => (total > 0 ? (part / total) * 100 : 0);

const remainingAfterMargin = (percent) => {
  const remaining = 1 - toNumber(percent) / 100;
  return remaining > 0 ? remaining : null;
};

export const pricing = ({
  perPackage,
  yourMargin,
  distributorMargin,
  brokerMargin,
  retailerMargin,
}) => {
  const yours = remainingAfterMargin(yourMargin);
  const middlemen = remainingAfterMargin(toNumber(distributorMargin) + toNumber(brokerMargin));
  const retailer = remainingAfterMargin(retailerMargin);

  const cost = toNumber(perPackage);
  const distributorPrice = cost > 0 && yours ? cost / yours : null;
  const wholesalePrice = distributorPrice && middlemen ? distributorPrice / middlemen : null;
  const retailPrice = wholesalePrice && retailer ? wholesalePrice / retailer : null;

  return { distributorPrice, wholesalePrice, retailPrice };
};

export const marginFromMsrp = ({
  perPackage,
  msrp,
  distributorMargin,
  brokerMargin,
  retailerMargin,
}) => {
  const price = toNumber(msrp);
  const cost = toNumber(perPackage);
  const middlemen = remainingAfterMargin(toNumber(distributorMargin) + toNumber(brokerMargin));
  const retailer = remainingAfterMargin(retailerMargin);

  if (price <= 0 || cost <= 0 || !middlemen || !retailer) return null;

  return 100 - (100 * cost) / (price * retailer * middlemen);
};

export const formatCost = (value, currency = "USD") => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "--";

  const amount = Number(value);
  const digits = amount !== 0 && Math.abs(amount) < 0.01 ? 4 : 2;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
};

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);

  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvRow = (cells) => cells.map(csvCell).join(",");

const round = (value) => Number(value).toFixed(2);

export const buildCsv = (state) => {
  const breakdown = costBreakdown(state.lines, state.packages);
  const prices = pricing({ ...state, perPackage: breakdown.perPackage });
  const rows = [
    ["ReciPal Recipe Cost Calculator"],
    ["Packages per batch", state.packages],
    ["Currency", state.currency],
    [],
    [
      "Item",
      "Type",
      "Quantity per batch",
      "Unit",
      "Price",
      "Quantity purchased",
      "Unit purchased",
      "Shipping",
      "Cost per batch",
      "Cost per package",
    ],
  ];

  state.lines.forEach((line, index) => {
    rows.push([
      line.name?.trim() || `Item ${index + 1}`,
      EXPENSE_TYPES.find(({ value }) => value === line.expenseType)?.label ?? line.expenseType,
      line.usedAmount,
      line.usedUnit,
      line.purchasePrice,
      line.purchaseAmount,
      line.purchaseUnit,
      line.purchaseShipping,
      round(lineCostPerBatch(line)),
      round(lineCostPerPackage(line, state.packages)),
    ]);
  });

  rows.push([]);
  rows.push(["Cost"]);
  rows.push(["Total per batch", round(breakdown.total)]);
  rows.push(["Total per package", round(breakdown.perPackage)]);

  EXPENSE_TYPES.forEach(({ value, label }) => {
    rows.push([
      `${label} per batch`,
      round(breakdown[value]),
      `${Math.round(percentOf(breakdown[value], breakdown.total))}%`,
    ]);
  });

  rows.push([]);
  rows.push(["Pricing"]);
  rows.push(["Your margin (%)", state.yourMargin]);
  rows.push(["Distributor margin (%)", state.distributorMargin]);
  rows.push(["Broker margin (%)", state.brokerMargin]);
  rows.push(["Retailer margin (%)", state.retailerMargin]);
  rows.push([
    "Price to distributors",
    prices.distributorPrice ? round(prices.distributorPrice) : "",
  ]);
  rows.push(["Price to retailers", prices.wholesalePrice ? round(prices.wholesalePrice) : ""]);
  rows.push(["Price to consumers (MSRP)", prices.retailPrice ? round(prices.retailPrice) : ""]);

  return rows.map(csvRow).join("\n");
};
